import React, { useState } from 'react';
import CalcShell from '../CalcShell';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function CalcButtons({ onCalc, onClear, testId }) {
  return (
    <div className="flex gap-2 mt-2">
      <button onClick={onCalc} data-testid={`${testId}-generate`} className="inline-flex items-center gap-2 px-5 py-2 rounded bg-[#4c7b25] hover:bg-[#4c7b25] text-white font-semibold text-sm shadow-sm">
        <span className="inline-block w-0 h-0 border-l-[7px] border-l-white border-y-[5px] border-y-transparent"></span> Generate
      </button>
      <button onClick={onClear} data-testid={`${testId}-clear`} className="px-5 py-2 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium text-sm">Clear</button>
    </div>
  );
}

function InputRow({ label, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] items-center gap-3 mb-3">
      <label className="text-sm text-slate-700">{label}</label>
      <div className="flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

function Histogram({ data, bins = 10 }) {
  if (!data || data.length === 0) return null;
  const nums = data.map(Number).filter(v => Number.isFinite(v));
  if (nums.length === 0) return null;
  const min = Math.min(...nums), max = Math.max(...nums);
  const width = (max - min) / bins || 1;
  const arr = Array.from({ length: bins }, (_, i) => ({
    bin: (min + i * width).toFixed(2) + '–' + (min + (i + 1) * width).toFixed(2),
    count: 0
  }));
  nums.forEach(v => {
    let idx = Math.floor((v - min) / width);
    if (idx >= bins) idx = bins - 1;
    if (idx < 0) idx = 0;
    arr[idx].count++;
  });
  return (
    <div className="mt-4">
      <div className="text-xs text-slate-500 mb-1">Distribution Histogram</div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={arr} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="bin" stroke="#64748b" fontSize={10} angle={-25} textAnchor="end" height={60} />
          <YAxis stroke="#64748b" fontSize={11} />
          <Tooltip contentStyle={{ background: '#0f172a', border: 'none', borderRadius: 6, color: '#fff', fontSize: 12 }} />
          <Bar dataKey="count" fill="#2563eb" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ResultPanel({ list, showHistogram }) {
  if (!list || list.length === 0) return null;
  return (
    <div className="mt-4 bg-white rounded-xl p-4 border border-slate-200">
      <div className="flex items-center gap-2 bg-[#4c7b25] text-white -mx-4 -mt-4 mb-3 px-4 py-2 rounded-t-xl">
        <span className="text-sm font-semibold">Result</span>
      </div>
      <div className="text-xs text-slate-500 mb-1">Generated {list.length} number{list.length === 1 ? '' : 's'}:</div>
      <div className="flex flex-wrap gap-2 max-h-56 overflow-auto">
        {list.map((v, i) => (
          <span key={i} className="px-2.5 py-1 rounded bg-blue-50 text-blue-700 font-mono text-sm border border-blue-100">{v}</span>
        ))}
      </div>
      {showHistogram && list.length > 3 && <Histogram data={list} />}
    </div>
  );
}

function BasicSection() {
  const [lo, setLo] = useState(1);
  const [hi, setHi] = useState(100);
  const [result, setResult] = useState([]);
  const gen = () => {
    const L = Math.min(lo, hi), H = Math.max(lo, hi);
    const n = Math.floor(Math.random() * (Math.floor(H) - Math.ceil(L) + 1)) + Math.ceil(L);
    setResult([n]);
  };
  const clear = () => { setLo(0); setHi(0); setResult([]); };
  return (
    <div className="mt-6 first:mt-0">
      <h3 className="text-lg font-bold text-slate-900">Random Number Generator</h3>
      <p className="text-sm text-slate-600 mt-1">This version of the generator creates a random integer. It can deal with very large integers up to a few thousand digits.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <InputRow label="Lower Limit"><input type="number" value={lo} onChange={e => setLo(+e.target.value || 0)} className="px-3 py-2 rounded border border-slate-300 bg-white w-full md:max-w-[220px]" /></InputRow>
        <InputRow label="Upper Limit"><input type="number" value={hi} onChange={e => setHi(+e.target.value || 0)} className="px-3 py-2 rounded border border-slate-300 bg-white w-full md:max-w-[220px]" /></InputRow>
        <CalcButtons testId="rand-basic" onCalc={gen} onClear={clear} />
        <ResultPanel list={result} showHistogram={false} />
      </div>
    </div>
  );
}

function ComprehensiveSection() {
  const [lo, setLo] = useState(0.2);
  const [hi, setHi] = useState(112.5);
  const [count, setCount] = useState(30);
  const [type, setType] = useState('decimal');
  const [precision, setPrecision] = useState(4);
  const [result, setResult] = useState([]);
  const gen = () => {
    const L = Math.min(lo, hi), H = Math.max(lo, hi);
    const N = Math.max(1, Math.min(10000, Math.floor(count)));
    const arr = [];
    for (let i = 0; i < N; i++) {
      if (type === 'integer') {
        arr.push(Math.floor(Math.random() * (Math.floor(H) - Math.ceil(L) + 1)) + Math.ceil(L));
      } else {
        const v = Math.random() * (H - L) + L;
        const p = Math.max(0, Math.min(15, Math.floor(precision)));
        arr.push(Number(v.toFixed(p)));
      }
    }
    setResult(arr);
  };
  const clear = () => { setLo(0); setHi(0); setCount(0); setPrecision(0); setResult([]); };
  return (
    <div className="mt-6">
      <h3 className="text-lg font-bold text-slate-900">Comprehensive Version</h3>
      <p className="text-sm text-slate-600 mt-1">This version of the generator can create one or many random integers or decimals. It can deal with very large numbers with up to 999 digits of precision.</p>
      <div className="mt-3 bg-slate-100 rounded-xl p-4">
        <InputRow label="Lower Limit"><input type="number" step="any" value={lo} onChange={e => setLo(+e.target.value || 0)} className="px-3 py-2 rounded border border-slate-300 bg-white w-full md:max-w-[220px]" /></InputRow>
        <InputRow label="Upper Limit"><input type="number" step="any" value={hi} onChange={e => setHi(+e.target.value || 0)} className="px-3 py-2 rounded border border-slate-300 bg-white w-full md:max-w-[220px]" /></InputRow>
        <InputRow label="Generate">
          <input type="number" value={count} onChange={e => setCount(+e.target.value || 0)} className="px-3 py-2 rounded border border-slate-300 bg-white w-40" />
          <span className="text-sm text-slate-700">numbers</span>
        </InputRow>
        <InputRow label="Type of result to generate?">
          <label className="flex items-center gap-1.5 text-sm"><input type="radio" checked={type === 'integer'} onChange={() => setType('integer')} /> Integer</label>
          <label className="flex items-center gap-1.5 text-sm"><input type="radio" checked={type === 'decimal'} onChange={() => setType('decimal')} /> Decimal</label>
        </InputRow>
        {type === 'decimal' && (
          <InputRow label="Precision">
            <input type="number" value={precision} onChange={e => setPrecision(+e.target.value || 0)} className="px-3 py-2 rounded border border-slate-300 bg-white w-40" />
            <span className="text-sm text-slate-700">digits</span>
          </InputRow>
        )}
        <CalcButtons testId="rand-comp" onCalc={gen} onClear={clear} />
        <ResultPanel list={result} showHistogram={true} />
      </div>
    </div>
  );
}

export default function RandomNumberCalc() {
  return (
    <CalcShell look="cnet" title="Random Number Generator" subtitle="Modify the values and click the Generate button to use">
      <BasicSection />
      <ComprehensiveSection />
    </CalcShell>
  );
}
